import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { GetObligationImpactsByParentIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetObligationImpactsByParentIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC obligation impact data to match the GraphQL query structure
 */
function mapTrpcObligationImpactsToGraphQL(
  trpcData: GetObligationImpactsByParentIdResponseRow[]
): GetObligationImpactsByParentIdQuery {
  return {
    obligation_impact: trpcData || [],
  };
}

export const useGetObligationImpactsByParentId = (
  parentId: string,
  options?: { skip?: boolean; onError?: (error: Error) => void }
) => {
  const { skip } = options || {};
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.obligationImpact.getByParentId.queryOptions({ parentId }),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData
    ? mapTrpcObligationImpactsToGraphQL(trpcData)
    : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
